import { useState, useEffect } from 'react';

// ── Detail section tree ─────────────────────────────────────────────────────────
// A compact, expandable left-rail tree for entity detail pages (client, job, …).
// Top-level nodes are the entity's sections (Jobs, Invoices, …); a node may carry
// `children` (individual records) that drill into the main panel. Selecting any
// node calls onSelect with its key — the page renders the matching content on the
// right. The main-section heading + record selection are both driven from here.

export interface TreeNode {
  key: string;
  label: string;
  icon?: string;
  count?: number;
  children?: TreeNode[];
}

interface DetailTreeNavProps {
  title: string;
  subtitle?: string;
  back?: { label: string; onClick: () => void };
  nodes: TreeNode[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Section keys whose children start expanded. */
  defaultExpanded?: string[];
}

export const DetailTreeNav = ({
  title,
  subtitle,
  back,
  nodes,
  activeKey,
  onSelect,
  defaultExpanded = [],
}: DetailTreeNavProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded));

  // Keep the branch containing the active node open so a drill-down stays visible.
  useEffect(() => {
    const parent = nodes.find(n => n.children?.some(c => c.key === activeKey));
    if (parent) setExpanded(prev => (prev.has(parent.key) ? prev : new Set(prev).add(parent.key)));
  }, [activeKey, nodes]);

  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
        {back && (
          <button
            onClick={back.onClick}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary transition-colors mb-1.5"
          >
            <span className="material-symbols-outlined text-sm">chevron_left</span>
            {back.label}
          </button>
        )}
        <p className="text-sm font-bold text-gray-900 truncate">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 truncate">{subtitle}</p>}
      </div>

      {/* Tree */}
      <nav className="flex-1 overflow-y-auto py-2">
        {nodes.map(node => {
          const hasChildren = !!node.children && node.children.length > 0;
          const isOpen = expanded.has(node.key);
          const active = activeKey === node.key;
          return (
            <div key={node.key}>
              <div
                className={`group flex items-center gap-1 mx-1.5 rounded-lg transition-colors ${
                  active ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {/* Expand chevron (only when there are children) */}
                {hasChildren ? (
                  <button
                    onClick={() => toggle(node.key)}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700"
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    <span className={`material-symbols-outlined text-base transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                      chevron_right
                    </span>
                  </button>
                ) : (
                  <span className="w-6 flex-shrink-0" />
                )}

                {/* Section row */}
                <button
                  onClick={() => onSelect(node.key)}
                  className="flex-1 flex items-center gap-2 py-2 pr-2 min-w-0 text-left"
                >
                  {node.icon && (
                    <span className={`material-symbols-outlined text-[18px] flex-shrink-0 ${active ? 'text-primary' : 'text-gray-400'}`}>
                      {node.icon}
                    </span>
                  )}
                  <span className="text-sm font-medium truncate">{node.label}</span>
                  {node.count !== undefined && node.count > 0 && (
                    <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      active ? 'bg-primary/15 text-primary' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {node.count}
                    </span>
                  )}
                </button>
              </div>

              {/* Children */}
              {hasChildren && isOpen && (
                <div className="mt-0.5 mb-1">
                  {node.children!.map(child => {
                    const childActive = activeKey === child.key;
                    return (
                      <button
                        key={child.key}
                        onClick={() => onSelect(child.key)}
                        className={`w-full flex items-center gap-2 py-1.5 pr-2 pl-9 mx-1.5 rounded-lg text-left transition-colors ${
                          childActive ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                        style={{ width: 'calc(100% - 0.75rem)' }}
                      >
                        {child.icon && (
                          <span className={`material-symbols-outlined text-[16px] flex-shrink-0 ${childActive ? 'text-primary' : 'text-gray-300'}`}>
                            {child.icon}
                          </span>
                        )}
                        <span className="text-[13px] truncate">{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};
